/** @fileoverview Synchronizes app draft updates with the live preview iframe. */

import { useCallback, useLayoutEffect, useRef } from 'react';
import type { FabrikAppDefinition } from '../../types/apps';

export default function usePreviewDraftBridge(appId: string) {
  const previewFrameRef = useRef<HTMLIFrameElement>(null);
  const previewReadyAppIdRef = useRef<string | null>(null);
  const pendingDraftRef = useRef<{ appId: string; definition: FabrikAppDefinition } | null>(null);

  const postDraftUpdate = useCallback((draftAppId: string, definition: FabrikAppDefinition) => {
    previewFrameRef.current?.contentWindow?.postMessage(
      { type: 'fabrik:app-draft-updated', appId: draftAppId, definition },
      window.location.origin
    );
  }, []);

  const onAppDraftUpdated = useCallback(
    (definition: FabrikAppDefinition) => {
      const update = { appId, definition };
      pendingDraftRef.current = update;
      if (previewReadyAppIdRef.current === appId) {
        postDraftUpdate(update.appId, update.definition);
      }
    },
    [appId, postDraftUpdate]
  );

  useLayoutEffect(() => {
    previewReadyAppIdRef.current = null;

    const handlePreviewMessage = (event: MessageEvent<unknown>) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== previewFrameRef.current?.contentWindow ||
        !event.data ||
        typeof event.data !== 'object'
      ) {
        return;
      }

      const message = event.data as { type?: unknown; appId?: unknown };
      if (message.type !== 'fabrik:preview-ready' || message.appId !== appId) return;

      previewReadyAppIdRef.current = appId;
      const pending = pendingDraftRef.current;
      if (pending?.appId === appId) postDraftUpdate(pending.appId, pending.definition);
    };

    window.addEventListener('message', handlePreviewMessage);
    return () => window.removeEventListener('message', handlePreviewMessage);
  }, [appId, postDraftUpdate]);

  return { previewFrameRef, onAppDraftUpdated };
}
